import { useEffect, useRef, useState } from 'react'
import { Form, Link, useLocation } from 'react-router'
import { Icon, type IconName } from '#app/components/ui/icon.tsx'
import { SITE_NAME } from '#app/utils/branding.ts'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalUser, userHasRole } from '#app/utils/user.ts'

const NAV_ITEMS = [
	{ label: 'Dashboard', to: '/dashboard' },
	{ label: 'New Assessment', to: '/assessment/new', icon: 'plus' as const },
	{ label: 'Purchase', to: '/pricing' },
] as const

const ADMIN_ITEM = { label: 'Admin', to: '/admin' } as const

/**
 * Animated hamburger icon that morphs between ☰ and ✕.
 * Three bars: top rotates 45°, middle fades, bottom rotates -45°.
 */
function HamburgerIcon({ open }: { open: boolean }) {
	return (
		<div className="relative h-5 w-6">
			<span
				className={cn(
					'absolute left-0 block h-[1.5px] w-full rounded-full bg-current transition-all duration-300 ease-[cubic-bezier(0.76,0,0.18,1)]',
					open ? 'top-[9px] rotate-45' : 'top-[2px] rotate-0',
				)}
			/>
			<span
				className={cn(
					'absolute left-0 top-[9px] block h-[1.5px] w-full rounded-full bg-current transition-all duration-200',
					open ? 'scale-x-0 opacity-0' : 'scale-x-100 opacity-100',
				)}
			/>
			<span
				className={cn(
					'absolute left-0 block h-[1.5px] w-full rounded-full bg-current transition-all duration-300 ease-[cubic-bezier(0.76,0,0.18,1)]',
					open ? 'top-[9px] -rotate-45' : 'top-[16px] rotate-0',
				)}
			/>
		</div>
	)
}

/**
 * Gold underline indicator for active nav links.
 * Uses a pseudo-element approach via a visible span that
 * slides in with a scale transform.
 */
function NavLink({
	to,
	label,
	icon,
	active,
	onClick,
}: {
	to: string
	label: string
	icon?: IconName
	active: boolean
	onClick?: () => void
}) {
	return (
		<Link
			to={to}
			onClick={onClick}
			className={cn(
				'group relative flex items-center gap-1.5 px-1 py-1.5 cathedral-heading text-xs tracking-[0.13em] uppercase transition-colors duration-200',
				active
					? 'text-cathedral-gold'
					: 'text-cathedral-dark/70 hover:text-cathedral-dark',
			)}
		>
			{icon ? <Icon name={icon} className="h-3.5 w-3.5" /> : null}
			{label}
			{/* Active indicator — gold bar */}
			<span
				className={cn(
					'absolute -bottom-1 left-0 h-[1.5px] w-full origin-left bg-cathedral-bright transition-transform duration-300 ease-[cubic-bezier(0.76,0,0.18,1)]',
					active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-50',
				)}
			/>
		</Link>
	)
}

export function AppNavbar() {
	const user = useOptionalUser()
	const location = useLocation()
	const [mobileOpen, setMobileOpen] = useState(false)
	const navRef = useRef<HTMLElement>(null)

	// Close mobile menu on route change
	useEffect(() => {
		setMobileOpen(false)
	}, [location.pathname])

	// Close on Escape
	useEffect(() => {
		if (!mobileOpen) return
		function onKey(e: KeyboardEvent) {
			if (e.key === 'Escape') setMobileOpen(false)
		}
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [mobileOpen])

	// Lock body scroll when mobile menu is open
	useEffect(() => {
		if (mobileOpen) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = ''
		}
		return () => {
			document.body.style.overflow = ''
		}
	}, [mobileOpen])

	if (!user) return null

	// Focus-mode: hide nav on assessment-taking pages (but not /assessment/new)
	const isAssessmentFocus =
		/^\/assessment\/[^/]+$/.test(location.pathname) &&
		location.pathname !== '/assessment/new'
	if (isAssessmentFocus) return null

	const isAdmin = userHasRole(user, 'admin')
	const isInAdminSection = location.pathname.startsWith('/admin')

	// ── Admin-mode: simplified navbar ──────────────────────────────────
	// When in /admin/*, the sidebar IS the navigation. The top bar just
	// provides branding, an escape hatch, and user controls.
	if (isInAdminSection) {
		return null // Admin layout has its own header
	}

	const allItems = isAdmin
		? [...NAV_ITEMS, ADMIN_ITEM]
		: NAV_ITEMS

	function isActive(to: string) {
		if (to === '/dashboard') return location.pathname === '/dashboard'
		return location.pathname.startsWith(to)
	}

	return (
		<nav
			ref={navRef}
			className="sticky top-0 z-50 border-b border-cathedral-gold/20 bg-cathedral-cream-2/95 backdrop-blur-md"
		>
			{/* Subtle top-edge glow line */}
			<div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cathedral-bright/25 to-transparent" />

			<div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-8">
				{/* Brand */}
				<Link
					to="/dashboard"
					className="group flex items-center gap-2.5 no-underline"
				>
					{/* Diamond mark */}
					<span className="relative flex h-7 w-7 items-center justify-center">
						<span className="absolute inset-0 rotate-45 rounded-[3px] border border-cathedral-gold/60 bg-cathedral-bright/10 transition-colors duration-300 group-hover:border-cathedral-gold group-hover:bg-cathedral-bright/20" />
						<span className="relative cathedral-heading text-[10px] font-bold tracking-wider text-cathedral-gold">
							IX
						</span>
					</span>
					<span className="hidden cathedral-heading text-xs tracking-[0.18em] uppercase text-cathedral-dark/80 transition-colors duration-200 group-hover:text-cathedral-dark sm:block">
						{SITE_NAME}
					</span>
				</Link>

				{/* Desktop nav links */}
				<div className="hidden items-center gap-6 md:flex">
					{allItems.map((item) => (
						<NavLink
							key={item.to}
							to={item.to}
							label={item.label}
							icon={'icon' in item ? item.icon : undefined}
							active={isActive(item.to)}
						/>
					))}

					{/* Separator */}
					<div className="mx-1 h-4 w-px bg-cathedral-bright/15" />

					{/* User section */}
					<div className="flex items-center gap-3">
						<Link
							to="/settings/profile"
							className={cn(
								'flex items-center gap-1.5 rounded-full border px-3 py-1.5 cathedral-heading text-[10px] tracking-[0.12em] uppercase transition-all duration-200',
								isActive('/settings')
									? 'border-cathedral-gold/40 bg-cathedral-bright/10 text-cathedral-gold'
									: 'border-cathedral-gold/15 text-cathedral-dark/60 hover:border-cathedral-gold/30 hover:text-cathedral-dark/80',
							)}
						>
							<Icon name="avatar" className="h-3.5 w-3.5" />
							<span className="max-w-[10ch] truncate">
								{user.name ?? user.username}
							</span>
						</Link>
						<Form action="/logout" method="POST">
							<button
								type="submit"
								className="flex items-center gap-1 rounded-sm px-2 py-1.5 cathedral-heading text-[10px] tracking-[0.1em] uppercase text-cathedral-dark/40 transition-colors duration-200 hover:text-cathedral-dark/70"
								aria-label="Log out"
							>
								<Icon name="exit" className="h-3.5 w-3.5" />
							</button>
						</Form>
					</div>
				</div>

				{/* Mobile hamburger */}
				<button
					type="button"
					className="relative z-50 text-cathedral-dark/80 transition-colors duration-200 hover:text-cathedral-gold md:hidden"
					onClick={() => setMobileOpen((o) => !o)}
					aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
					aria-expanded={mobileOpen}
				>
					<HamburgerIcon open={mobileOpen} />
				</button>
			</div>

			{/* Mobile overlay + menu */}
			<div
				className={cn(
					'fixed inset-0 z-40 bg-cathedral-deep/35 backdrop-blur-sm transition-opacity duration-300 md:hidden',
					mobileOpen
						? 'pointer-events-auto opacity-100'
						: 'pointer-events-none opacity-0',
				)}
				onClick={() => setMobileOpen(false)}
				aria-hidden="true"
			/>
			<div
				className={cn(
					'fixed inset-x-0 top-0 z-40 flex flex-col bg-cathedral-cream-2 border-b border-cathedral-gold/15 pt-16 pb-6 px-6 transition-all duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] md:hidden',
					mobileOpen
						? 'translate-y-0 opacity-100'
						: '-translate-y-full opacity-0',
				)}
			>
				{/* Decorative divider */}
				<div className="mb-5 flex items-center gap-3">
					<div className="h-px flex-1 bg-gradient-to-r from-cathedral-bright/30 to-transparent" />
					<div className="h-1.5 w-1.5 rotate-45 bg-cathedral-bright/40" />
					<div className="h-px flex-1 bg-gradient-to-l from-cathedral-bright/30 to-transparent" />
				</div>

				{allItems.map((item, i) => (
					<Link
						key={item.to}
						to={item.to}
						onClick={() => setMobileOpen(false)}
						className={cn(
							'flex items-center gap-2 rounded-lg px-3 py-3.5 cathedral-heading text-sm tracking-[0.12em] uppercase no-underline transition-all duration-200',
							isActive(item.to)
								? 'bg-cathedral-bright/10 text-cathedral-gold'
								: 'text-cathedral-dark/70 hover:bg-cathedral-bright/5 hover:text-cathedral-dark',
						)}
						style={{
							transitionDelay: mobileOpen ? `${80 + i * 40}ms` : '0ms',
							opacity: mobileOpen ? 1 : 0,
							transform: mobileOpen ? 'translateX(0)' : 'translateX(-12px)',
						}}
					>
						{'icon' in item ? (
							<Icon name={item.icon} className="h-4 w-4" />
						) : null}
						{item.label}
						{isActive(item.to) ? (
							<span className="ml-auto h-1.5 w-1.5 rounded-full bg-cathedral-bright" />
						) : null}
					</Link>
				))}

				{/* Bottom section: profile + logout */}
				<div
					className="mt-4 border-t border-cathedral-gold/10 pt-4"
					style={{
						transitionDelay: mobileOpen
							? `${80 + allItems.length * 40}ms`
							: '0ms',
						opacity: mobileOpen ? 1 : 0,
						transform: mobileOpen ? 'translateX(0)' : 'translateX(-12px)',
						transition: 'opacity 200ms, transform 200ms',
					}}
				>
					<Link
						to="/settings/profile"
						onClick={() => setMobileOpen(false)}
						className={cn(
							'flex items-center gap-2 rounded-lg px-3 py-3 cathedral-heading text-sm tracking-[0.12em] uppercase no-underline transition-colors duration-200',
							isActive('/settings')
								? 'text-cathedral-gold'
								: 'text-cathedral-dark/60 hover:text-cathedral-dark/80',
						)}
					>
						<Icon name="avatar" className="h-4 w-4" />
						{user.name ?? user.username}
					</Link>
					<Form action="/logout" method="POST">
						<button
							type="submit"
							className="flex w-full items-center gap-2 rounded-lg px-3 py-3 cathedral-heading text-sm tracking-[0.12em] uppercase text-cathedral-dark/40 transition-colors duration-200 hover:text-cathedral-dark/60"
						>
							<Icon name="exit" className="h-4 w-4" />
							Log out
						</button>
					</Form>
				</div>
			</div>
		</nav>
	)
}
